/**
 * Lazy UUID source: Playwright specs import this module under plain Node,
 * where `expo-crypto` resolves to TypeScript source inside node_modules and
 * crashes the loader. Prefer the platform crypto (Node ≥19, browsers, Hermes)
 * and only require expo-crypto when `crypto.randomUUID` is unavailable.
 */
const randomUUID = () => {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }
    return require('expo-crypto').randomUUID();
};

/**
 * Derive a user-facing fieldId from a label: lowercase, non-alphanumerics → `_`,
 * trimmed of leading/trailing underscores.
 */
const deriveFieldId = (label) => {
    if (!label) return '';
    return String(label)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
};

/**
 * Ensure a fieldId is unique within a form schema, appending `_2`, `_3`, etc.
 * on collision. `excludeFieldId` skips a field's own current id when editing.
 */
const ensureUniqueFieldId = (fieldId, schema, excludeFieldId = null) => {
    const existing = collectFieldIds(schema).filter((id) => id !== excludeFieldId);
    let candidate = fieldId || 'field';
    let suffix = 2;
    while (existing.includes(candidate)) {
        candidate = `${fieldId}_${suffix}`;
        suffix += 1;
    }
    return candidate;
};

/**
 * Collect every user-defined fieldId across all sections of a schema.
 */
const collectFieldIds = (schema) => {
    const ids = [];
    if (!schema?.sections) return ids;
    for (const section of schema.sections) {
        for (const field of section.fields || []) {
            if (field.fieldId) ids.push(field.fieldId);
        }
    }
    return ids;
};

/**
 * Flatten all fields across all sections into a single list.
 */
const flattenFields = (schema) => {
    if (!schema?.sections) return [];
    return schema.sections.flatMap((section) => section.fields || []);
};

const FIELD_TYPE_DEFAULTS = {
    text: { config: {} },
    longtext: { config: {} },
    number: { config: {} },
    date: { config: {} },
    datetime: { config: {} }, // legacy — no longer offered in the picker, kept for existing schemas
    time: { config: {} },
    dropdown: { config: { options: [] } },
    checkbox: { config: {} },
    checkboxGroup: { config: { options: [] } },
    file: { config: { allowMultiple: false, maxSizeMB: 10, accept: [] } },
    email: { config: { autofillLoggedIn: true } },
    phone: { config: { autofillLoggedIn: true } },
    address: { config: { autofillLoggedIn: true } },
    name: { config: { linkToPeople: true, autofillLoggedIn: true } },
    payment: { config: { amountCents: null, fundID: null, allowedChannels: 'both' } },
    signature: {
        // Signatures default to required — an answered signature always
        // requires explicit consent (see formSignaturePolicy).
        required: true,
        config: {
            allowedSignerRoles: ['self', 'parentGuardian'],
            agreementText: '',
            consentVersion: 'reach-esign-v1',
        },
    },
    profile: {
        config: { viewID: null, showViewToRespondent: false },
    },
};

/**
 * Build a new field object with sensible defaults for the given type.
 */
const createField = ({ type, label = '', sectionId = null }) => {
    const fieldId = ensureUniqueFieldId(deriveFieldId(label), { sections: [] });
    return {
        id: `fld_${randomUUID()}`,
        fieldId,
        type,
        label,
        description: '',
        placeholder: '',
        defaultValue: null,
        hidden: false,
        required: FIELD_TYPE_DEFAULTS[type]?.required ?? false,
        config: FIELD_TYPE_DEFAULTS[type]?.config
            ? JSON.parse(JSON.stringify(FIELD_TYPE_DEFAULTS[type].config))
            : {},
        ...(sectionId ? { _sectionId: sectionId } : {}),
    };
};

/**
 * Build a new section object with an empty fields array.
 */
const createSection = ({ title = 'New Section' } = {}) => ({
    id: `sec_${randomUUID()}`,
    title,
    description: '',
    allowAddMore: false,
    addMoreLabel: 'Add Another',
    fields: [],
});

/**
 * Deep-clone a section with fresh `id`s for the section and every field, and
 * unique `fieldId`s across the whole schema. Used by the builder's "Duplicate
 * section" action. The clone is inserted immediately after the source.
 */
const cloneSection = (section, schema) => {
    const cloned = JSON.parse(JSON.stringify(section));
    cloned.id = `sec_${randomUUID()}`;
    // Track existing fieldIds so each cloned field gets a unique one.
    const existingIds = collectFieldIds(schema);
    for (const field of cloned.fields || []) {
        field.id = `fld_${randomUUID()}`;
        let candidate = field.fieldId || 'field';
        let suffix = 2;
        while (existingIds.includes(candidate)) {
            candidate = `${field.fieldId || 'field'}_${suffix}`;
            suffix += 1;
        }
        field.fieldId = candidate;
        existingIds.push(candidate);
        if (field._sectionId) field._sectionId = cloned.id;
    }
    return cloned;
};

/**
 * Deep-clone a single field with a fresh `id` (`fld_<uuid>`). The `fieldId`
 * is made unique by appending `_copy` (not a numeric suffix, so the intent
 * is human-obvious). If `_copy` itself collides with an existing fieldId,
 * numeric disambiguation kicks in: `fieldId_copy_2`, `fieldId_copy_3`, etc.
 * Used by the builder's "Duplicate field" action.
 */
const cloneField = (field, schema) => {
    const cloned = JSON.parse(JSON.stringify(field));
    cloned.id = `fld_${randomUUID()}`;
    const existingIds = collectFieldIds(schema);
    let candidate = `${field.fieldId || 'field'}_copy`;
    let suffix = 2;
    while (existingIds.includes(candidate)) {
        candidate = `${field.fieldId || 'field'}_copy_${suffix}`;
        suffix += 1;
    }
    cloned.fieldId = candidate;
    existingIds.push(candidate);
    return cloned;
};

/**
 * Build a fresh empty schema (version 3).
 */
const createEmptySchema = () => ({
    version: 3,
    sections: [],
});

/**
 * True when any section of the schema contains a field of the given type.
 * Used to enforce the "no profile fields on public forms" guard and similar
 * schema-level rules.
 */
const schemaHasFieldType = (schema, type) =>
    flattenFields(schema).some((field) => field.type === type);

export {
    deriveFieldId,
    ensureUniqueFieldId,
    collectFieldIds,
    flattenFields,
    createField,
    createSection,
    cloneSection,
    cloneField,
    createEmptySchema,
    schemaHasFieldType,
    FIELD_TYPE_DEFAULTS,
};
